/** Sticky-left stacking for user-pinned table columns (web + mobile). */

const PINNED_CLASS = "sfe-col-pinned";
const PINNED_EDGE_CLASS = "sfe-col-pinned-edge";

function clearPinnedStyles(table: HTMLTableElement): void {
  for (const cell of table.querySelectorAll<HTMLElement>(`.${PINNED_CLASS}`)) {
    cell.classList.remove(PINNED_CLASS, PINNED_EDGE_CLASS);
    cell.style.removeProperty("left");
    cell.style.removeProperty("z-index");
  }
}

function pinnedKeysFromTable(table: HTMLTableElement): string[] {
  const attr = table.getAttribute("data-sfe-pinned");
  if (attr != null) {
    return attr
      .split(",")
      .map((key) => key.trim())
      .filter(Boolean);
  }
  const fromCols = [...table.querySelectorAll("colgroup col[data-pinned='1']")]
    .map((col) => col.getAttribute("data-col") ?? "")
    .filter(Boolean);
  if (fromCols.length) return fromCols;

  // Fall back to header cells marked during render.
  const head = table.tHead?.rows[0];
  if (!head) return [];
  return [...head.cells]
    .map((cell) =>
      cell.hasAttribute("data-pinned") || cell.classList.contains(PINNED_CLASS)
        ? cell.getAttribute("data-col") ?? ""
        : "",
    )
    .filter(Boolean);
}

function cellsForColumn(table: HTMLTableElement, key: string): HTMLElement[] {
  const safe = key.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return [...table.querySelectorAll<HTMLElement>(`[data-col="${safe}"]`)];
}

function measureColumnWidth(table: HTMLTableElement, key: string): number {
  const cells = cellsForColumn(table, key);
  let width = 0;
  for (const cell of cells) {
    const measured = cell.offsetWidth || cell.getBoundingClientRect().width;
    if (measured > width) width = measured;
  }
  if (width > 0) return width;
  const safe = key.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  const col = table.querySelector<HTMLElement>(`colgroup col[data-col="${safe}"]`);
  if (col) {
    const measured = col.offsetWidth || col.getBoundingClientRect().width;
    if (measured > 0) return measured;
    const styleWidth = Number.parseFloat(col.style.width || "");
    if (Number.isFinite(styleWidth) && styleWidth > 0) return styleWidth;
  }
  return 0;
}

/** Apply sticky left stacking for pinned columns inside a table. */
export function applyPinnedColumnSticky(table: HTMLTableElement): void {
  clearPinnedStyles(table);
  const pinnedKeys = pinnedKeysFromTable(table);
  if (!pinnedKeys.length) {
    table.classList.remove("sfe-table-has-pinned");
    table.removeAttribute("data-sfe-pin-remeasure");
    table.removeAttribute("data-sfe-pin-sig");
    return;
  }

  const pinSig = pinnedKeys.join(",");
  if (table.getAttribute("data-sfe-pin-sig") !== pinSig) {
    table.setAttribute("data-sfe-pin-sig", pinSig);
    table.removeAttribute("data-sfe-pin-remeasure");
  }

  table.classList.add("sfe-table-has-pinned");
  let left = 0;
  pinnedKeys.forEach((key, index) => {
    const width = measureColumnWidth(table, key);
    const isEdge = index === pinnedKeys.length - 1;
    const z = 6 + pinnedKeys.length - index;
    for (const cell of cellsForColumn(table, key)) {
      cell.classList.add(PINNED_CLASS);
      if (isEdge) cell.classList.add(PINNED_EDGE_CLASS);
      cell.style.left = `${left}px`;
      cell.style.zIndex = String(cell.tagName === "TH" ? z + 2 : z);
    }
    left += width;
  });
  // Layout may not be ready on first paint — remeasure once so stacked left offsets are correct.
  if (pinnedKeys.length > 1 && table.getAttribute("data-sfe-pin-remeasure") == null) {
    table.setAttribute("data-sfe-pin-remeasure", "1");
    requestAnimationFrame(() => {
      table.setAttribute("data-sfe-pin-remeasure", "done");
      if (table.isConnected) applyPinnedColumnSticky(table);
    });
  }
}

/** Sync pinned sticky styles onto a cloned sticky float header table. */
export function syncPinnedColumnStickyToFloat(
  source: HTMLTableElement,
  floatTable: HTMLTableElement,
): void {
  clearPinnedStyles(floatTable);
  if (!source.classList.contains("sfe-table-has-pinned")) return;
  const pinnedKeys = pinnedKeysFromTable(source);
  if (!pinnedKeys.length) return;

  floatTable.classList.add("sfe-table-has-pinned");
  floatTable.setAttribute("data-sfe-pinned", pinnedKeys.join(","));
  let left = 0;
  pinnedKeys.forEach((key, index) => {
    const width = measureColumnWidth(source, key) || measureColumnWidth(floatTable, key);
    const isEdge = index === pinnedKeys.length - 1;
    const z = 8 + pinnedKeys.length - index;
    for (const cell of cellsForColumn(floatTable, key)) {
      cell.classList.add(PINNED_CLASS);
      if (isEdge) cell.classList.add(PINNED_EDGE_CLASS);
      cell.style.left = `${left}px`;
      cell.style.zIndex = String(z);
    }
    left += width;
  });
}

export function tablePinnedAttr(pinnedKeys: string[]): string {
  return ` data-sfe-pinned="${pinnedKeys.join(",")}"`;
}

/** Observe tables under root and keep pinned sticky offsets up to date. */
export function observePinnedColumnSticky(root: HTMLElement): () => void {
  let raf = 0;
  const schedule = (): void => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      for (const table of root.querySelectorAll("table")) {
        if (!(table instanceof HTMLTableElement)) continue;
        if (
          table.getAttribute("data-sfe-pinned") != null ||
          table.querySelector("colgroup col[data-pinned='1']") ||
          table.classList.contains("recs") ||
          table.classList.contains("sfe-mobile-options-table")
        ) {
          applyPinnedColumnSticky(table);
        }
      }
    });
  };

  const observer = new MutationObserver(schedule);
  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-sfe-pinned", "data-pinned", "style", "class"] });
  window.addEventListener("resize", schedule, { passive: true });
  schedule();

  return () => {
    if (raf) cancelAnimationFrame(raf);
    observer.disconnect();
    window.removeEventListener("resize", schedule);
  };
}
